/**
 * SourceBadge — where a factual answer came from.
 *
 * Renders one quiet pill under the reply:
 *
 *   ◦ 2 sources ⌄
 *
 * Tap → the sources themselves, each a link you can open:
 *
 *   Open-Meteo · Bengaluru, IN        ↗   👍 👎
 *   ISRO tests SSLV booster — The Hindu ↗  👍 👎
 *
 * **No confidence wording, no scores.** (29 Aug 2026, on the user's call:
 * a companion that tells you it has "low confidence" in itself makes people
 * trust it less, and the number was meaningless to a non-technical reader.)
 * Rammy still scores every source internally to order and filter results —
 * the user just never reads the maths. The 👍/👎 still teach those scores
 * per-user via `/api/credibility/feedback`.
 *
 * Props (from useChatStream → MessageBubble / HeroChat / VoiceMode):
 *   sources     = [{ source: "open_meteo", title: "Open-Meteo · …", url }]
 *   credibility = optional; only used to fill in sources the links list
 *                 didn't name. Never displayed.
 *   query       = the question this reply answered (required by feedback API)
 */
import React, { useMemo, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";

const _prettyName = (src) => {
  const map = {
    tmdb: "TMDB", youtube: "YouTube", youtube_trailers: "YouTube (trailers)",
    youtube_reviews: "YouTube (reviews)", currents: "Currents",
    wikidata: "Wikidata", wikipedia: "Wikipedia", crossref: "Crossref",
    semantic_scholar: "Semantic Scholar", sec_edgar: "SEC EDGAR",
    edgar: "SEC EDGAR", google_places: "Google Places", osm: "OpenStreetMap",
    tavily: "Web search", duckduckgo: "DuckDuckGo", open_meteo: "Open-Meteo",
    yahoo_finance: "Yahoo Finance", coingecko: "CoinGecko",
    frankfurter: "Frankfurter", worldtimeapi: "WorldTimeAPI",
    exchange_rate: "Open Exchange Rates", iana_tz: "IANA time zones",
    ip_api: "IP Geo", google_geocoding: "Google Geocoding",
    google_calendar: "Google Calendar", gmail: "Gmail", google_drive: "Drive",
    rammy_memory: "Rammy Memory",
  };
  return map[src] || src;
};

export default function SourceBadge({ credibility, sources: links, query, apiBase }) {
  const [open, setOpen] = useState(false);

  const rows = useMemo(() => {
    const byKey = new Map();
    (Array.isArray(links) ? links : []).forEach((l) => {
      const key = l.source || l.url || l.title;
      if (!key || byKey.has(key + (l.url || ""))) return;
      byKey.set(key + (l.url || ""), {
        key,
        name: l.title || _prettyName(l.source || "") || l.url,
        url: l.url || null,
      });
    });
    // The count is the LIST, and nothing else. A scored-but-linkless source
    // used to get its own row here, which is how a reply showed "4 sources"
    // while the model itself had looked nothing up (QA defect D02). If it
    // isn't a source we can show the user, it isn't a source we can count.
    return [...byKey.values()];
  }, [links]);

  const count = rows.length;
  if (!count) return null;

  const submitFeedback = async (source_id, was_wrong) => {
    const q = (query || "").trim().slice(0, 400) || "this answer";
    try {
      const res = await fetch(`${apiBase || ""}/api/credibility/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_id, was_wrong, query: q }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast(was_wrong ? "Noted — I'll lean on that one less." : "Thanks — noted.",
            { icon: was_wrong ? "🙏" : "✨" });
    } catch (_) {
      toast.error("Couldn't save that just now — try again in a moment.");
    }
  };

  return (
    <div className="mt-1.5" data-testid="source-badge">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="group inline-flex items-center gap-1.5 rounded-full border border-[var(--rm-border)]
                   bg-[var(--rm-bg-subtle)] px-2.5 py-1 text-[11px] font-medium text-[var(--rammy-text-sec)]
                   transition-[background-color,box-shadow,transform] duration-200
                   hover:-translate-y-[1px] hover:shadow-sm hover:text-[var(--rammy-text)]
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
                   focus-visible:ring-[var(--rammy-cyan)]"
        aria-expanded={open}
        aria-label={`${count} source${count === 1 ? "" : "s"} — tap to see them`}
        data-testid="source-badge-pill"
      >
        <L.Link2 size={11} className="text-[var(--rammy-cyan-dark)]" aria-hidden/>
        <span>{count} {count === 1 ? "source" : "sources"}</span>
        <L.ChevronDown
          size={12}
          className={`transition-transform duration-200 ${open ? "rotate-180" : "group-hover:translate-y-[1px]"}`}
        />
      </button>

      {open && (
        <div
          className="mt-2 rounded-xl border border-[var(--rm-border)] bg-[var(--rm-surface)]/70
                     backdrop-blur-sm px-3 py-2 text-[12px] shadow-sm fade-up"
          data-testid="source-badge-details"
        >
          <ul className="divide-y divide-[var(--rm-border)]">
            {rows.map(({ key, name, url }, idx) => {
              const Name = url ? "a" : "span";
              return (
                <li
                  key={`${key}-${idx}`}
                  className="py-1.5 flex items-center gap-2 fade-up"
                  style={{ animationDelay: `${idx * 45}ms` }}
                >
                  <Name
                    {...(url ? { href: url, target: "_blank", rel: "noreferrer noopener" } : {})}
                    className={`flex-1 truncate text-[var(--rammy-text)] ${url ? "hover:text-[var(--rammy-cyan-dark)] hover:underline decoration-dotted underline-offset-2 transition-colors" : ""}`}
                    title={name}
                    data-testid={`badge-source-link-${key}`}
                  >
                    {name}
                    {url && <L.ArrowUpRight size={10} className="inline ml-0.5 opacity-60"/>}
                  </Name>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => submitFeedback(key, false)}
                      className="p-1 rounded transition-colors hover:bg-emerald-50 active:scale-95"
                      aria-label={`${name} was helpful`}
                      data-testid={`badge-thumbs-up-${key}`}
                    >
                      <L.ThumbsUp size={11} className="text-emerald-600"/>
                    </button>
                    <button
                      type="button"
                      onClick={() => submitFeedback(key, true)}
                      className="p-1 rounded transition-colors hover:bg-red-50 active:scale-95"
                      aria-label={`${name} was wrong`}
                      data-testid={`badge-thumbs-down-${key}`}
                    >
                      <L.ThumbsDown size={11} className="text-red-500"/>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-1.5 text-[10px] text-[var(--rammy-text-muted)] leading-relaxed">
            Tap a source to open it. Mark one wrong and Rammy leans on it less.
          </div>
        </div>
      )}
    </div>
  );
}
